import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import type { ScheduledEvent, CrewMember, SchedulingConflict } from "./types";
import type { SchedulingEvent, SchedulingEventType, SchedulingAuditEntry, SchedulingStateProposal, SchedulingAttribution } from "./eventBus";
import { parseISO } from "date-fns";
import { useAppData } from "@/data/AppDataContext";

// ── Feature Flag ──
export const FEATURE_SCHEDULING = true;

interface SchedulingContextValue {
  // ── Core state (scheduling-owned only) ──
  events: ScheduledEvent[];
  crews: CrewMember[];
  addEvent: (e: ScheduledEvent) => void;
  updateEvent: (id: string, patch: Partial<ScheduledEvent>) => void;
  removeEvent: (id: string) => void;
  getEventsForDate: (date: Date) => ScheduledEvent[];
  getEventsForCrew: (crewId: string) => ScheduledEvent[];
  getEventsForJob: (jobId: string) => ScheduledEvent[];
  conflicts: SchedulingConflict[];

  // ── Event-driven integration ──
  emitEvent: (type: SchedulingEventType, jobId: string, eventId: string, payload?: Record<string, unknown>) => void;
  proposeStateChange: (proposal: Omit<SchedulingStateProposal, "timestamp">) => void;
  pendingProposals: SchedulingStateProposal[];
  eventLog: SchedulingEvent[];
  auditTrail: SchedulingAuditEntry[];
}

const SchedulingContext = createContext<SchedulingContextValue | undefined>(undefined);

function detectConflicts(events: ScheduledEvent[]): SchedulingConflict[] {
  const conflicts: SchedulingConflict[] = [];
  const active = events.filter(e => e.status !== "cancelled" && e.status !== "completed");

  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i], b = active[j];
      if (a.crewId !== b.crewId) continue;
      const aStart = parseISO(a.startTime).getTime();
      const aEnd = parseISO(a.endTime).getTime();
      const bStart = parseISO(b.startTime).getTime();
      const bEnd = parseISO(b.endTime).getTime();

      if (aStart < bEnd && bStart < aEnd) {
        conflicts.push({ eventA: a.id, eventB: b.id, crewId: a.crewId, type: "overlap" });
      }
    }
  }
  return conflicts;
}

export function SchedulingProvider({ children }: { children: React.ReactNode }) {
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [crews, setCrews] = useState<CrewMember[]>([]);
  const [eventLog, setEventLog] = useState<SchedulingEvent[]>([]);
  const [auditTrail, setAuditTrail] = useState<SchedulingAuditEntry[]>([]);
  const [pendingProposals, setPendingProposals] = useState<SchedulingStateProposal[]>([]);

  // ── Upstream attribution resolver ──
  // Reads the latest jobs + sales opportunities from AppData and produces a
  // SchedulingAttribution snapshot. Missing fields are left undefined; never
  // fabricated. Kept as a ref-less inline lookup because it's called inside
  // setEvents callbacks where stale-closure risk is low (AppData updates
  // re-render this provider and rebuild emitEvent via useCallback deps).
  const { jobs, salesOpportunities, contractors, crews: appCrews } = useAppData();

  // Build schedule projection from DB-backed jobs (main UI contract preserved).
  useEffect(() => {
    const projected: ScheduledEvent[] = jobs
      .filter((j) => !!j.scheduledStartTime && !!j.scheduledEndTime)
      .map((j) => {
        const eventType: ScheduledEvent["eventType"] =
          j.currentStage === "QA Review" ? "inspection" :
          j.currentStage === "In Progress" || j.currentStage === "Assigned and Dispatched" ? "install" :
          "pre_install";
        const status: ScheduledEvent["status"] =
          j.currentStage === "Completed" ? "completed" :
          j.currentStage === "In Progress" ? "in_progress" :
          j.currentStage === "Scheduled Confirmed" || j.dispatchStatus === "Assigned" ? "confirmed" :
          j.currentStage === "Scheduled Tentative" ? "scheduled" :
          "draft";
        const crewName = j.assignedCrew || j.assignedSubcontractor || "Unassigned";
        const crewId =
          String(j.crewId ?? "").trim() ||
          (crewName ? `crew-${crewName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "");
        const crewType: ScheduledEvent["crewType"] = j.assignedSubcontractor ? "subcontractor" : "internal";
        return {
          id: `job-${j.id}`,
          jobId: j.id,
          eventType,
          title: `${j.projectType} Install`,
          startTime: j.scheduledStartTime!,
          endTime: j.scheduledEndTime!,
          crewId,
          crewName,
          crewType,
          status,
          department: "install",
          assignedTo: j.salesRep || undefined,
          createdAt: j.createdAt,
          updatedAt: j.updatedAt,
        };
      });
    setEvents((prev) => (prev.length === 0 ? projected : prev));

    const crewsFromJobs = new Map<string, CrewMember>();
    for (const c of appCrews) {
      const id = c.id || `crew-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      crewsFromJobs.set(id, {
        id,
        name: c.name,
        type: c.contractorId ? "subcontractor" : "internal",
        color: c.contractorId ? "hsl(24 90% 52%)" : "hsl(207 80% 42%)",
        active: c.active !== false,
      });
    }
    for (const j of jobs) {
      const crewName = j.assignedCrew || j.assignedSubcontractor;
      if (!crewName) continue;
      const id = String(j.crewId ?? "").trim() || `crew-${crewName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      if (crewsFromJobs.has(id)) continue;
      crewsFromJobs.set(id, {
        id,
        name: crewName,
        type: j.assignedSubcontractor ? "subcontractor" : "internal",
        color: j.assignedSubcontractor ? "hsl(24 90% 52%)" : "hsl(207 80% 42%)",
        active: true,
      });
    }
    // Include active contractors as assignable crews even before first assignment.
    for (const c of contractors) {
      if (!c.active) continue;
      const crewName = c.companyName;
      const id = `crew-${crewName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      if (crewsFromJobs.has(id)) continue;
      crewsFromJobs.set(id, {
        id,
        name: crewName,
        type: "subcontractor",
        color: "hsl(24 90% 52%)",
        active: true,
      });
    }
    setCrews(Array.from(crewsFromJobs.values()));
  }, [jobs, contractors, appCrews]);
  const resolveAttribution = useCallback((jobId: string): SchedulingAttribution => {
    const job = jobs.find(j => j.id === jobId);
    const opportunityId = job?.opportunityId;
    const opp = opportunityId ? salesOpportunities.find(o => o.id === opportunityId) : undefined;
    return {
      jobId,
      opportunityId: opportunityId || undefined,
      salesRep: job?.salesRep || undefined,
      leadSource: opp?.leadSource || undefined,
      resolvedAt: new Date().toISOString(),
    };
  }, [jobs, salesOpportunities]);

  // ── Audit logger ──
  const logAudit = useCallback((type: SchedulingEventType, jobId: string, eventId: string, detail: string, metadata?: Record<string, unknown>) => {
    const entry: SchedulingAuditEntry = {
      id: `sched-audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type, jobId, eventId,
      timestamp: new Date().toISOString(),
      actor: "system",
      detail,
      metadata,
    };
    setAuditTrail(prev => [entry, ...prev]);
  }, []);

  // ── Event emitter (does NOT touch core state) ──
  // Enriches every emission with upstream attribution so downstream consumers
  // can trace any scheduling outcome back to sales rep / lead source / opp.
  const emitEvent = useCallback((type: SchedulingEventType, jobId: string, eventId: string, payload: Record<string, unknown> = {}) => {
    const attribution = jobId ? resolveAttribution(jobId) : undefined;
    const enrichedPayload = attribution ? { ...payload, attribution } : payload;
    const evt: SchedulingEvent = {
      id: `sched-evt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type, jobId, eventId,
      timestamp: new Date().toISOString(),
      payload: enrichedPayload,
      acknowledged: false,
    };
    setEventLog(prev => [evt, ...prev]);
    logAudit(type, jobId, eventId, `Event emitted: ${type}`, enrichedPayload);
  }, [logAudit, resolveAttribution]);

  // ── State proposal (scheduling proposes, core decides) ──
  const proposeStateChange = useCallback((proposal: Omit<SchedulingStateProposal, "timestamp">) => {
    const full: SchedulingStateProposal = { ...proposal, timestamp: new Date().toISOString() };
    setPendingProposals(prev => [...prev, full]);
    logAudit(
      "INSTALL_SCHEDULED" as SchedulingEventType,
      proposal.jobId,
      proposal.sourceEventId,
      `State change proposed: ${proposal.proposedField} → ${proposal.proposedValue} (${proposal.reason})`
    );
  }, [logAudit]);

  // ── CRUD with audit logging ──
  const addEvent = useCallback((e: ScheduledEvent) => {
    setEvents(prev => [...prev, e]);
    emitEvent("INSTALL_SCHEDULED", e.jobId, e.id, { eventType: e.eventType, crewId: e.crewId });
  }, [emitEvent]);

  const updateEvent = useCallback((id: string, patch: Partial<ScheduledEvent>) => {
    setEvents(prev => {
      const existing = prev.find(e => e.id === id);
      if (!existing) return prev;

      const updated = { ...existing, ...patch, updatedAt: new Date().toISOString() };

      // Emit appropriate events based on what changed
      if (patch.status === "confirmed" && existing.status !== "confirmed") {
        emitEvent("INSTALL_CONFIRMED", existing.jobId, id, { previousStatus: existing.status });
      }
      if (patch.status === "in_progress" && existing.status !== "in_progress") {
        emitEvent("INSTALL_STARTED", existing.jobId, id);
      }
      if (patch.status === "completed" && existing.status !== "completed") {
        emitEvent("INSTALL_COMPLETED", existing.jobId, id);
      }
      if (patch.status === "cancelled" && existing.status !== "cancelled") {
        emitEvent("INSTALL_CANCELLED", existing.jobId, id);
      }
      if (patch.crewId && patch.crewId !== existing.crewId) {
        emitEvent("CREW_REASSIGNED", existing.jobId, id, {
          previousCrewId: existing.crewId,
          newCrewId: patch.crewId,
        });
      }
      if (patch.startTime && patch.startTime !== existing.startTime) {
        emitEvent("EVENT_RESCHEDULED", existing.jobId, id, {
          previousStart: existing.startTime,
          newStart: patch.startTime,
        });
      }

      // ── Extension detection ──
      // An "extension" is a span-growing change where startTime did NOT change
      // (or moved earlier) AND the resulting duration is greater than before.
      // Reuses EVENT_RESCHEDULED with payload.subtype="extension" — no new
      // enum value, no new entity.
      const prevStartMs = parseISO(existing.startTime).getTime();
      const prevEndMs = parseISO(existing.endTime).getTime();
      const nextStartMs = parseISO(updated.startTime).getTime();
      const nextEndMs = parseISO(updated.endTime).getTime();
      const prevDurationMin = Math.round((prevEndMs - prevStartMs) / 60000);
      const nextDurationMin = Math.round((nextEndMs - nextStartMs) / 60000);
      const startTimeUnchanged = !patch.startTime || patch.startTime === existing.startTime;
      const endTimeChanged = !!patch.endTime && patch.endTime !== existing.endTime;
      const spanGrew = nextDurationMin > prevDurationMin;
      if (startTimeUnchanged && endTimeChanged && spanGrew) {
        emitEvent("EVENT_RESCHEDULED", existing.jobId, id, {
          subtype: "extension",
          previousStart: existing.startTime,
          previousEnd: existing.endTime,
          newStart: updated.startTime,
          newEnd: updated.endTime,
          previousDurationMin: prevDurationMin,
          newDurationMin: nextDurationMin,
          addedMin: nextDurationMin - prevDurationMin,
          crewId: updated.crewId,
        });
      }

      return prev.map(e => e.id === id ? updated : e);
    });
  }, [emitEvent]);

  const removeEvent = useCallback((id: string) => {
    setEvents(prev => {
      const existing = prev.find(e => e.id === id);
      if (existing) {
        emitEvent("INSTALL_CANCELLED", existing.jobId, id, { action: "removed" });
      }
      return prev.filter(e => e.id !== id);
    });
  }, [emitEvent]);

  const getEventsForDate = useCallback((date: Date) => {
    const dayStr = date.toISOString().slice(0, 10);
    return events.filter(e => e.startTime.slice(0, 10) === dayStr);
  }, [events]);

  const getEventsForCrew = useCallback((crewId: string) => {
    return events.filter(e => e.crewId === crewId);
  }, [events]);

  const getEventsForJob = useCallback((jobId: string) => {
    return events.filter(e => e.jobId === jobId);
  }, [events]);

  const conflicts = useMemo(() => detectConflicts(events), [events]);

  // Emit conflict events through emitEvent so attribution attaches and the
  // event lands in eventLog (not just auditTrail). CONFLICT_RESOLVED is
  // intentionally deferred — see eventBus attribution notes.
  useMemo(() => {
    conflicts.forEach(c => {
      const conflictingEvent = events.find(e => e.id === c.eventA);
      const jobId = conflictingEvent?.jobId ?? "";
      emitEvent("CONFLICT_DETECTED", jobId, c.eventA, {
        conflictWith: c.eventB,
        crewId: c.crewId,
        conflictType: c.type,
      });
    });
  }, [conflicts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({
    events, crews, addEvent, updateEvent, removeEvent,
    getEventsForDate, getEventsForCrew, getEventsForJob, conflicts,
    emitEvent, proposeStateChange, pendingProposals, eventLog, auditTrail,
  }), [events, crews, addEvent, updateEvent, removeEvent, getEventsForDate, getEventsForCrew, getEventsForJob, conflicts, emitEvent, proposeStateChange, pendingProposals, eventLog, auditTrail]);

  return <SchedulingContext.Provider value={value}>{children}</SchedulingContext.Provider>;
}

export function useScheduling() {
  const ctx = useContext(SchedulingContext);
  if (!ctx) throw new Error("useScheduling must be used within SchedulingProvider");
  return ctx;
}
